import { TrendBadge } from '@/components/stats/TrendBadge';
import { getComparisonText } from '@/lib/metric-utils';
import { HStack, type StackProps, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { MetricValue } from './MetricValue';
import type { StatsMetric } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface MetricHeaderProps extends StackProps {
  title: string;
  metric?: StatsMetric;
  formatAsPercent?: boolean;
  /** Hide trend and comparison text. Use when trend data isn't meaningful for the metric. */
  hideTrend?: boolean;
  /** When true, renders "N/A" instead of the metric value (e.g. for partner brands without click tracking). */
  isUnavailable?: boolean;
  /** Optional info tooltip text displayed next to the label */
  infoTip?: string;
  /** Maximum fraction digits for currency formatting */
  maximumFractionDigits?: number;
}

/**
 * Reusable metric header: label, value, and trend text
 *
 * Layout:
 *   Label (muted)
 *   $4,143.59
 *   +34% vs last 30 days
 */
export const MetricHeader: React.FC<MetricHeaderProps> = ({
  title,
  metric,
  formatAsPercent,
  hideTrend = false,
  isUnavailable = false,
  infoTip,
  maximumFractionDigits,
  ...props
}) => {
  const { t } = useTranslation();

  const hasData = metric !== undefined;

  const comparisonText = hasData && !hideTrend ? getComparisonText(metric, t) : undefined;

  return (
    <VStack alignItems="flex-start" gap={0.5} {...props}>
      {infoTip ? (
        <Tooltip content={infoTip} showArrow portalled positioning={{ placement: 'top' }}>
          <Text
            as="span"
            w="fit-content"
            fontSize="11px"
            fontWeight="semibold"
            color="fg.muted"
            letterSpacing="0.06em"
            textTransform="uppercase"
            cursor="help"
            css={{
              textDecorationLine: 'underline',
              textDecorationStyle: 'dotted',
              textDecorationColor: 'currentColor',
              textUnderlineOffset: '3px',
            }}
          >
            {title}
          </Text>
        </Tooltip>
      ) : (
        <Text
          fontSize="11px"
          fontWeight="semibold"
          color="fg.muted"
          letterSpacing="0.06em"
          textTransform="uppercase"
        >
          {title}
        </Text>
      )}
      <Text
        fontSize={{ base: 'xl', md: '2xl' }}
        fontFamily="heading"
        fontWeight="semibold"
        letterSpacing="-0.01em"
        css={{ fontVariantNumeric: 'tabular-nums' }}
        color={isUnavailable ? 'fg.muted' : undefined}
      >
        {isUnavailable ? (
          t('dashboard.stats.notAvailable')
        ) : (
          <MetricValue
            metric={metric}
            formatAsPercent={formatAsPercent}
            maximumFractionDigits={maximumFractionDigits}
          />
        )}
      </Text>
      {!hideTrend && hasData && !isUnavailable && (
        <HStack gap={1} alignItems="baseline">
          {metric.trend && <TrendBadge trend={metric.trend} />}
          {comparisonText && (
            <Text fontSize="xs" color="fg.muted">
              {comparisonText}
            </Text>
          )}
        </HStack>
      )}
    </VStack>
  );
};
